import { processLegsStrikes } from '#/unified-rfqs/ui/rfq-builder/leg-validation';
import { DraftOptionLeg } from '#/unified-rfqs/ui/rfq-builder/types';

interface BaseStrategyLeg {
  /** Non-zero integer, negative included. From buyer's perspective */
  readonly ratio?: number;
  readonly action?: 'BUY' | 'SELL';
  readonly quantity?: string;
}

export interface OptionStrategyLeg extends BaseStrategyLeg {
  readonly optionKind: 'CALL' | 'PUT';
}

export interface FutureStrategyLeg extends BaseStrategyLeg {
  // If leg is a hedge leg then it should have price
  readonly price?: string;
}

export type StrategyShortCode =
  | 'CM'
  | 'CL'
  | 'CB'
  | 'CC'
  | 'CS'
  | 'PT'
  | 'PB'
  | 'PC'
  | 'PS'
  | 'CR'
  | 'PR'
  | 'SD'
  | 'SG'
  | 'FT'
  | 'FS'
  | 'FD'
  | 'PD'
  | 'CD'
  | 'FF';

type OptionStrategyCode =
  | 'Call'
  | 'CFly'
  | 'CCal'
  | 'CSpd'
  | 'Put'
  | 'PFly'
  | 'PCal'
  | 'PSpd'
  | 'RRCall'
  | 'RRPut'
  | 'Strd'
  | 'Strg'
  | 'ICondor'
  | 'PCondor'
  | 'CCondor'
  | 'IFly';

type FutureStrategyCode = 'Future' | 'FSpd';

type CustomStrategyCode = 'Custom';

export type StrategyCode = OptionStrategyCode | FutureStrategyCode;

interface BaseStrategy {
  readonly shortCode: StrategyShortCode;
  readonly code: StrategyCode;
  readonly name: string;
  readonly kind: 'OPTION' | 'FUTURE';
  readonly legs: readonly BaseStrategyLeg[];
  /**
   * Defines how legs expirations work in relation to other legs
   * * `'independent'`: no dependency between legs expirations
   * * `'mirror'`: all legs expirations should be the same
   */
  readonly expirationPolicy: 'independent' | 'mirror';
}

export interface OptionStrategy extends BaseStrategy {
  readonly kind: 'OPTION';
  readonly code: OptionStrategyCode;
  readonly legs: readonly OptionStrategyLeg[];
  readonly hedgeLegs: readonly FutureStrategyLeg[];
  /**
   * Defines how legs expiration values are validated
   * * `'asc'`: must be in ascending order
   * * `'none'`: no validation required
   */
  readonly expirationValidationPolicy: 'asc' | 'none';
  /**
   * Defines how legs strikes work in relation to other legs
   * * `'independent'`: no dependency between legs strikes
   * * `'mirror'`: all legs strikes should be the same
   */
  readonly strikePolicy: 'independent' | 'mirror';
  /**
   * Defines how legs strike values are validated
   * * `'asc'`: must be in ascending order
   * * `'desc'`: must be in descending order
   * * `'same'`: must be all the same value
   * * `'diff'`: must be all different value
   * * `'custom'`: custom validation
   * * `'none'`: no validation required
   */
  readonly strikeValidationPolicy:
    | 'asc'
    | 'desc'
    | 'same'
    | 'diff'
    | 'custom'
    | 'none';
  /**
   * Custom strike validation function.
   * This is applied when `strikeValidationPolicy` is set to `'custom'`
   * @param legs
   * @returns boolean
   */
  readonly validateStrikes?: (legs: readonly DraftOptionLeg[]) => boolean;
}

interface FutureStrategy extends BaseStrategy {
  readonly kind: 'FUTURE';
  readonly code: FutureStrategyCode;
  readonly legs: readonly FutureStrategyLeg[];
}

export type Strategy = OptionStrategy | FutureStrategy;

export const STRATEGIES_STRICT: Record<StrategyCode, Strategy> = {
  Call: {
    shortCode: 'CL',
    code: 'Call',
    name: 'Call',
    kind: 'OPTION',
    legs: [{ ratio: 1, optionKind: 'CALL' }],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'independent',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'none',
  },
  CFly: {
    shortCode: 'CB',
    code: 'CFly',
    name: 'Call Butterfly',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'CALL' },
      { ratio: -2, optionKind: 'CALL' },
      { ratio: 1, optionKind: 'CALL' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'asc',
  },
  CCal: {
    shortCode: 'CC',
    code: 'CCal',
    name: 'Call Calendar',
    kind: 'OPTION',
    legs: [
      { ratio: -1, optionKind: 'CALL' },
      { ratio: 1, optionKind: 'CALL' },
    ],
    hedgeLegs: [{ ratio: 1 }, { ratio: 1 }],
    expirationPolicy: 'independent',
    expirationValidationPolicy: 'asc',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'none',
  },
  CCondor: {
    shortCode: 'CD',
    code: 'CCondor',
    name: 'Call Condor',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'CALL' },
      { ratio: -1, optionKind: 'CALL' },
      { ratio: -1, optionKind: 'CALL' },
      { ratio: 1, optionKind: 'CALL' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'asc',
  },
  CSpd: {
    shortCode: 'CS',
    code: 'CSpd',
    name: 'Call Spread',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'CALL' },
      { ratio: -1, optionKind: 'CALL' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'asc',
  },
  Put: {
    shortCode: 'PT',
    code: 'Put',
    name: 'Put',
    kind: 'OPTION',
    legs: [{ ratio: 1, optionKind: 'PUT' }],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'independent',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'none',
  },
  PFly: {
    shortCode: 'PB',
    code: 'PFly',
    name: 'Put Butterfly',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'PUT' },
      { ratio: -2, optionKind: 'PUT' },
      { ratio: 1, optionKind: 'PUT' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'asc',
  },
  PCal: {
    shortCode: 'PC',
    code: 'PCal',
    name: 'Put Calendar',
    kind: 'OPTION',
    legs: [
      { ratio: -1, optionKind: 'PUT' },
      { ratio: 1, optionKind: 'PUT' },
    ],
    hedgeLegs: [{ ratio: 1 }, { ratio: 1 }],
    expirationPolicy: 'independent',
    expirationValidationPolicy: 'asc',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'none',
  },
  PCondor: {
    shortCode: 'PD',
    code: 'PCondor',
    name: 'Put Condor',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'PUT' },
      { ratio: -1, optionKind: 'PUT' },
      { ratio: -1, optionKind: 'PUT' },
      { ratio: 1, optionKind: 'PUT' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'asc',
  },
  PSpd: {
    shortCode: 'PS',
    code: 'PSpd',
    name: 'Put Spread',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'PUT' },
      { ratio: -1, optionKind: 'PUT' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'desc',
  },
  RRCall: {
    shortCode: 'CR',
    code: 'RRCall',
    name: 'Risk Reversal (Call)',
    kind: 'OPTION',
    legs: [
      { ratio: -1, optionKind: 'PUT' },
      { ratio: 1, optionKind: 'CALL' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'none',
  },
  RRPut: {
    shortCode: 'PR',
    code: 'RRPut',
    name: 'Risk Reversal (Put)',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'PUT' },
      { ratio: -1, optionKind: 'CALL' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'none',
  },
  Strd: {
    shortCode: 'SD',
    code: 'Strd',
    name: 'Straddle',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'PUT' },
      { ratio: 1, optionKind: 'CALL' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'mirror',
    strikeValidationPolicy: 'same',
  },
  Strg: {
    shortCode: 'SG',
    code: 'Strg',
    name: 'Strangle',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'PUT' },
      { ratio: 1, optionKind: 'CALL' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'diff',
  },
  IFly: {
    shortCode: 'FF',
    code: 'IFly',
    name: 'Iron Butterfly',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'PUT' },
      { ratio: -1, optionKind: 'PUT' },
      { ratio: -1, optionKind: 'CALL' },
      { ratio: 1, optionKind: 'CALL' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'custom',
    validateStrikes: (legs) => {
      const strikes = processLegsStrikes(legs);
      if (strikes.length !== 4) return true;
      return (
        strikes[0]! < strikes[1]! &&
        strikes[1]! === strikes[2]! &&
        strikes[2]! < strikes[3]!
      );
    },
  },
  ICondor: {
    shortCode: 'FD',
    code: 'ICondor',
    name: 'Iron Condor',
    kind: 'OPTION',
    legs: [
      { ratio: 1, optionKind: 'PUT' },
      { ratio: -1, optionKind: 'PUT' },
      { ratio: -1, optionKind: 'CALL' },
      { ratio: 1, optionKind: 'CALL' },
    ],
    hedgeLegs: [{ ratio: 1 }],
    expirationPolicy: 'mirror',
    expirationValidationPolicy: 'none',
    strikePolicy: 'independent',
    strikeValidationPolicy: 'asc',
  },
  Future: {
    shortCode: 'FT',
    code: 'Future',
    name: 'Future',
    kind: 'FUTURE',
    legs: [{ ratio: 1 }],
    expirationPolicy: 'independent',
  },
  FSpd: {
    shortCode: 'FS',
    code: 'FSpd',
    name: 'Futures Spread',
    kind: 'FUTURE',
    legs: [{ ratio: -1 }, { ratio: 1 }],
    expirationPolicy: 'independent',
  },
};

export const CUSTOM_STRATEGY: Omit<
  Strategy,
  'code' | 'legs' | 'expirationPolicy' | 'kind'
> & { code: CustomStrategyCode } = {
  shortCode: 'CM',
  code: 'Custom',
  name: 'Custom',
};

export const STRATEGIES: Record<string, Strategy> = STRATEGIES_STRICT;

export const getStrategyOptions = (includeCustom = false) => {
  const predefinedStrategies = Object.values(STRATEGIES);
  const strategies = includeCustom
    ? [CUSTOM_STRATEGY, ...predefinedStrategies]
    : predefinedStrategies;

  return strategies.map((strategy) => ({
    value: strategy.shortCode,
    label: strategy.name,
  }));
};
